import {
  addDays,
  startOfDay,
  isAfter,
  eachMinuteOfInterval,
  subMinutes,
  isSameDay,
} from 'date-fns';
import {
  IOfficeHoursWeekRule,
  IOfficeHoursWeekRuleWithId,
  TDay,
  TTimeInterval,
} from 'types/e-visits';
import { IFormValues } from './office-hours-edit.schema';

export function getInitialValues({
  officeHours,
  expertiseCode,
  medicalInstitutionId,
}: {
  officeHours?: IOfficeHoursWeekRuleWithId | IOfficeHoursWeekRule;
  expertiseCode: string;
  medicalInstitutionId: string;
}): IFormValues {
  if (officeHours) {
    const keepTo = officeHours.validTo ? isAfter(new Date(officeHours.validTo), Date.now()) : false;
    const minDate = addDays(startOfDay(Date.now()), 1);
    const start = officeHours.validFrom ? new Date(officeHours.validFrom) : minDate;
    const canKeep = isAfter(start, minDate) || isSameDay(start, minDate);

    return {
      ...officeHours,
      workspaceId: medicalInstitutionId,
      validityFrom: true,
      validityTo: keepTo,
      validTo: keepTo ? officeHours.validTo : undefined,
      validFrom: canKeep ? start.toISOString() : minDate.toISOString(),
    };
  }

  return {
    days: [...new Array(7).keys()].reduce((acc, key) => {
      acc[key as TDay] = [];

      return acc;
    }, {} as IFormValues['days']),
    expertiseCode,
    workspaceId: medicalInstitutionId,
    validityFrom: true,
    validityTo: false,
    validFrom: addDays(startOfDay(Date.now()), 1).toISOString(),
  };
}

export function generateDisableTimes(values: TTimeInterval[]) {
  const disabledMinutes = new Map<number, Set<number>>(
    [...new Array(24).keys()].map((k) => [k, new Set()]),
  );

  values.forEach((v) => {
    if (v.to && v.from) {
      const start = new Date(v.from);
      const end = new Date(v.to);

      const intervals = eachMinuteOfInterval({
        start: start,
        end: subMinutes(end, 1),
      });

      intervals.forEach((i) => disabledMinutes.get(i.getHours())?.add(i.getMinutes()));
    }
  });

  return {
    disabledMinutes: (hour: number) => {
      return Array.from(disabledMinutes.get(hour) || []);
    },
    disabledHours: () => {
      return [
        ...Array.from(disabledMinutes.keys()).map((k) =>
          [0, 15, 30, 45].every((v) => disabledMinutes.get(Number(k))?.has(v))
            ? Number(k)
            : undefined,
        ),
      ].filter((h) => h !== undefined) as number[];
    },
  };
}
